import { EXTERNAL_API_MODE } from '../constants/externalApi'
import { INTENT_TAG_MODE } from '../constants/intentTag'
import {
  EVENT_TRIGGER_ACTION,
  EVENT_TRIGGER_ACTION_OPTIONS,
  EVENT_TRIGGER_COLLAB_SEAT,
  EVENT_TRIGGER_COLLAB_SEAT_OPTIONS,
  EVENT_TRIGGER_FIELD,
  EVENT_TRIGGER_FIELD_OPTIONS,
  EVENT_TRIGGER_OP,
  EVENT_TRIGGER_OPS_DURATION,
  EVENT_TRIGGER_OPS_FUNCTION_NAME,
  EVENT_TRIGGER_OPS_TEXT,
  EVENT_TRIGGER_SMS_CHANNELS,
} from '../constants/eventTrigger'
import { getExternalApiDisplayName } from './externalApiHelpers'

const FIELD_LABELS = EVENT_TRIGGER_FIELD_OPTIONS.reduce((map, item) => {
  map[item.value] = item.label
  return map
}, {})

const OP_LABELS = [
  ...EVENT_TRIGGER_OPS_TEXT,
  ...EVENT_TRIGGER_OPS_DURATION,
  ...EVENT_TRIGGER_OPS_FUNCTION_NAME,
].reduce((map, item) => {
  map[item.value] = item.label
  return map
}, {})

const ACTION_LABELS = EVENT_TRIGGER_ACTION_OPTIONS.reduce((map, item) => {
  map[item.value] = item.label
  return map
}, {})

function generateEventTriggerId() {
  return `et_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 9)}`
}

function normalizeEventTriggerField(raw) {
  if (raw === EVENT_TRIGGER_FIELD.CALL_DURATION) return EVENT_TRIGGER_FIELD.CALL_DURATION
  if (raw === EVENT_TRIGGER_FIELD.FUNCTION_NAME) return EVENT_TRIGGER_FIELD.FUNCTION_NAME
  return EVENT_TRIGGER_FIELD.INTENT_NAME
}

/** once_only 默认开启；仅明确为否时关闭 */
export function parseEventTriggerOnceOnly(raw) {
  if (raw === false || raw === 0 || raw === '0' || raw === 'false' || raw === 'no' || raw === '否') {
    return false
  }
  return true
}

function normalizeConditionRow(raw) {
  const row = raw && typeof raw === 'object' ? raw : {}
  const field = normalizeEventTriggerField(row.field)
  const validOps = getOpsForEventTriggerField(field).map((item) => item.value)
  const op = validOps.includes(row.op)
    ? row.op
    : field === EVENT_TRIGGER_FIELD.CALL_DURATION
      ? EVENT_TRIGGER_OP.GT
      : EVENT_TRIGGER_OP.EQ
  return {
    field,
    op,
    value: row.value != null ? String(row.value) : '',
  }
}

export function getOpsForEventTriggerField(field) {
  if (field === EVENT_TRIGGER_FIELD.CALL_DURATION) return EVENT_TRIGGER_OPS_DURATION
  if (field === EVENT_TRIGGER_FIELD.FUNCTION_NAME) return EVENT_TRIGGER_OPS_FUNCTION_NAME
  return EVENT_TRIGGER_OPS_TEXT
}

export function isEventTriggerOpLocked(field) {
  return field === EVENT_TRIGGER_FIELD.FUNCTION_NAME
}

export function createDefaultEventTriggerCondition() {
  return {
    field: EVENT_TRIGGER_FIELD.INTENT_NAME,
    op: EVENT_TRIGGER_OP.EQ,
    value: '',
  }
}

export function createDefaultEventTrigger() {
  return normalizeEventTrigger({
    conditions: [createDefaultEventTriggerCondition()],
    action: undefined,
    api_id: undefined,
    sms_channel_id: undefined,
    sms_params: '',
    collab_seat_type: undefined,
    collab_target: '',
    once_only: true,
  })
}

export function normalizeEventTrigger(raw) {
  const item = raw && typeof raw === 'object' ? raw : {}
  let conditions = []
  if (Array.isArray(item.conditions) && item.conditions.length) {
    conditions = item.conditions.map(normalizeConditionRow)
  } else if (item.field || item.op || item.value) {
    conditions = [
      normalizeConditionRow({
        field: item.field,
        op: item.op,
        value: item.value,
      }),
    ]
  } else {
    conditions = [createDefaultEventTriggerCondition()]
  }

  const action = EVENT_TRIGGER_ACTION_OPTIONS.some((opt) => opt.value === item.action)
    ? item.action
    : item.api_id || item.apiId
      ? EVENT_TRIGGER_ACTION.API_CALL
      : ''

  const rawSeatType =
    item.collab_seat_type != null ? item.collab_seat_type : item.collabSeatType
  const collabSeatType = EVENT_TRIGGER_COLLAB_SEAT_OPTIONS.some((opt) => opt.value === rawSeatType)
    ? rawSeatType
    : ''

  return {
    id: item.id != null && String(item.id).trim() !== '' ? String(item.id) : generateEventTriggerId(),
    conditions,
    action,
    api_id:
      action === EVENT_TRIGGER_ACTION.API_CALL
        ? String(item.api_id != null ? item.api_id : item.apiId || '')
        : '',
    sms_channel_id:
      action === EVENT_TRIGGER_ACTION.SMS
        ? String(item.sms_channel_id != null ? item.sms_channel_id : item.smsChannelId || '')
        : '',
    sms_params:
      action === EVENT_TRIGGER_ACTION.SMS
        ? String(item.sms_params != null ? item.sms_params : item.smsParams || '')
        : '',
    collab_seat_type: action === EVENT_TRIGGER_ACTION.COLLAB_REMIND ? collabSeatType : '',
    collab_target:
      action === EVENT_TRIGGER_ACTION.COLLAB_REMIND &&
      collabSeatType === EVENT_TRIGGER_COLLAB_SEAT.THIRD_PARTY
        ? String(item.collab_target != null ? item.collab_target : item.collabTarget || '')
        : '',
    once_only: parseEventTriggerOnceOnly(
      item.once_only != null ? item.once_only : item.onceOnly,
    ),
  }
}

export function buildExternalApiOptions(externalApisJson, externalApisForm) {
  const opts = []
  ;(Array.isArray(externalApisForm) ? externalApisForm : []).forEach((item) => {
    if (!item || !item.id) return
    const name = getExternalApiDisplayName(item)
    opts.push({
      id: String(item.id),
      label: name === '未命名' ? '未命名·表单' : name,
      source: EXTERNAL_API_MODE.FORM,
    })
  })
  ;(Array.isArray(externalApisJson) ? externalApisJson : []).forEach((item) => {
    if (!item || !item.id) return
    const name = getExternalApiDisplayName(item)
    opts.push({
      id: String(item.id),
      label: name === '未命名' ? '未命名·JSON' : name,
      source: EXTERNAL_API_MODE.JSON,
    })
  })
  return opts
}

export function findExternalApiOptionLabel(apiId, externalApisJson, externalApisForm) {
  const id = apiId != null ? String(apiId) : ''
  if (!id) return '（未选择）'
  const opts = buildExternalApiOptions(externalApisJson, externalApisForm)
  const found = opts.find((opt) => opt.id === id)
  return found ? found.label : '（接口已删除）'
}

export function findSmsChannelLabel(channelId) {
  const id = channelId != null ? String(channelId) : ''
  const found = EVENT_TRIGGER_SMS_CHANNELS.find((item) => item.id === id)
  return found ? found.name : '（通道已删除）'
}

export function findCollabSeatLabel(seatType) {
  const value = seatType != null ? String(seatType) : ''
  const found = EVENT_TRIGGER_COLLAB_SEAT_OPTIONS.find((item) => item.value === value)
  return found ? found.label : '（未选择）'
}

export function formatSingleEventTriggerCondition(cond) {
  const normalized = normalizeConditionRow(cond)
  const field = FIELD_LABELS[normalized.field] || normalized.field || '条件'
  const op = OP_LABELS[normalized.op] || normalized.op || ''
  let value = normalized.value || ''
  if (normalized.field === EVENT_TRIGGER_FIELD.CALL_DURATION && value) {
    value = `${value}秒`
  }
  return `${field} ${op} 「${value}」`
}

export function formatEventTriggerConditions(item) {
  const normalized = normalizeEventTrigger(item)
  // 产品约定：列表多条条件为「或」
  return normalized.conditions.map(formatSingleEventTriggerCondition).join(' 或 ')
}

export function formatEventTriggerAction(item, externalApisJson, externalApisForm) {
  const normalized = normalizeEventTrigger(item)
  const label = ACTION_LABELS[normalized.action] || '未选择'
  if (normalized.action === EVENT_TRIGGER_ACTION.API_CALL) {
    return `${label}（${findExternalApiOptionLabel(normalized.api_id, externalApisJson, externalApisForm)}）`
  }
  if (normalized.action === EVENT_TRIGGER_ACTION.SMS) {
    return `${label}（${findSmsChannelLabel(normalized.sms_channel_id)}）`
  }
  if (normalized.action === EVENT_TRIGGER_ACTION.COLLAB_REMIND) {
    const seatLabel = findCollabSeatLabel(normalized.collab_seat_type)
    if (normalized.collab_seat_type === EVENT_TRIGGER_COLLAB_SEAT.THIRD_PARTY) {
      const target = String(normalized.collab_target || '').trim()
      return target ? `${label}（${seatLabel}：${target}）` : `${label}（${seatLabel}）`
    }
    return normalized.collab_seat_type ? `${label}（${seatLabel}）` : label
  }
  return label
}

export function formatEventTriggerOnceOnly(item) {
  const normalized = normalizeEventTrigger(item)
  return normalized.once_only ? '是' : '否'
}

export function buildEventTriggersSnapshot(eventTriggers) {
  return (Array.isArray(eventTriggers) ? eventTriggers : []).map((item) => {
    const normalized = normalizeEventTrigger(item)
    return {
      id: normalized.id,
      conditions: normalized.conditions.map((cond) => ({
        field: cond.field,
        op: cond.op || EVENT_TRIGGER_OP.EQ,
        value: String(cond.value || '').trim(),
      })),
      action: normalized.action,
      api_id: normalized.api_id,
      sms_channel_id: normalized.sms_channel_id,
      sms_params: normalized.sms_params,
      collab_seat_type: normalized.collab_seat_type,
      collab_target: normalized.collab_target,
      once_only: !!normalized.once_only,
    }
  })
}

export function applyEventTriggersFromSnapshot(raw) {
  if (!Array.isArray(raw)) return []
  return raw.map(normalizeEventTrigger)
}

function isForcedIntentTagMode(intentTag) {
  const mode =
    intentTag && intentTag.mode != null ? String(intentTag.mode).trim().toLowerCase() : ''
  return mode === INTENT_TAG_MODE.FORCED || mode === 'forced_extraction'
}

function collectNamedOptions(list) {
  const names = []
  const seen = {}
  ;(Array.isArray(list) ? list : []).forEach((item) => {
    const name = item && item.name != null ? String(item.name).trim() : ''
    if (!name || seen[name]) return
    seen[name] = true
    names.push(name)
  })
  return names
}

/** 常规模式取意向标签，强制抽取取评级规则 */
export function collectIntentTagNameOptions(intentTag) {
  if (isForcedIntentTagMode(intentTag)) {
    return collectNamedOptions(intentTag && intentTag.ratingRules)
  }
  return collectNamedOptions(intentTag && intentTag.rules)
}

export function getIntentNameValuePlaceholder(intentTag) {
  return isForcedIntentTagMode(intentTag) ? '请选择评级规则' : '请选择意向标签'
}
